import { Controller } from "@hotwired/stimulus"

// Owns the dashboard hero session scrubber: dot strip + chevrons + ←/→
// keys + the rack-focus transition. Each "dot" is a session; clicking one
// fetches the rendered hero HTML for that session and swaps it into the
// hero card during a 180ms fade-out / 240ms fade-in.
//
// The element this controller is attached to IS the dot strip; the hero
// card lives elsewhere on the page. We find it by scoping a query to a
// shared ancestor (the dashboard show template wraps both in the same
// container).
export default class extends Controller {
  static targets = ["dot", "olderButton", "newerButton"]
  static values = {
    activeId: String,
    total: Number,
    fetchUrl: String,
  }

  static FADE_OUT_MS = 180
  static FADE_IN_MS = 240

  connect() {
    this.heroCard = document.querySelector(
      '[data-session-scrubber-target="hero"]',
    )
    this.heroContent = this.heroCard?.querySelector(
      '[data-session-scrubber-target="content"]',
    )
    this.fetching = false
    this.refreshChevrons()
  }

  // Index convention: realIndex 0 = newest. The visual order in the strip
  // is reversed (oldest left, newest right), but the underlying data array
  // and this index match summaries[0] = newest.
  activeIndex() {
    const dot = this.dotTargets.find((d) => d.dataset.active === "true")
    return dot ? parseInt(dot.dataset.realIndex, 10) : 0
  }

  jumpTo(event) {
    const dot = event.currentTarget
    const realIndex = parseInt(dot.dataset.realIndex, 10)
    const sessionId = dot.dataset.sessionId
    if (Number.isNaN(realIndex) || !sessionId) return
    if (dot.dataset.active === "true") return
    this.swapTo(sessionId, realIndex)
  }

  goOlder() {
    const next = this.activeIndex() + 1
    if (next >= this.totalValue) return
    const dot = this.dotTargets.find(
      (d) => parseInt(d.dataset.realIndex, 10) === next,
    )
    if (!dot) return
    this.swapTo(dot.dataset.sessionId, next)
  }

  goNewer() {
    const next = this.activeIndex() - 1
    if (next < 0) return
    const dot = this.dotTargets.find(
      (d) => parseInt(d.dataset.realIndex, 10) === next,
    )
    if (!dot) return
    this.swapTo(dot.dataset.sessionId, next)
  }

  onKey(event) {
    const tag = event.target?.tagName
    if (
      tag === "INPUT" ||
      tag === "TEXTAREA" ||
      event.target?.isContentEditable
    )
      return
    if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.goOlder()
    } else if (event.key === "ArrowRight") {
      event.preventDefault()
      this.goNewer()
    }
  }

  async swapTo(sessionId, realIndex) {
    if (!this.heroContent || this.fetching) return
    this.fetching = true

    this.heroContent.classList.add("hero-swap--out")

    // Kick off the fetch in parallel with the fade-out so we don't pay
    // the network cost on top of the 180ms animation.
    const url = `${this.fetchUrlValue}?session_id=${sessionId}`
    const fetchPromise = fetch(url, {
      headers: { Accept: "text/html" },
      credentials: "same-origin",
    }).then((res) => (res.ok ? res.text() : null))

    const [html] = await Promise.all([
      fetchPromise,
      this.wait(this.constructor.FADE_OUT_MS),
    ])

    if (html != null) {
      this.heroContent.innerHTML = html
    }

    // Trigger reflow so the transition replays for the fade-in.
    void this.heroContent.offsetHeight
    this.heroContent.classList.remove("hero-swap--out")

    this.updateActive(sessionId, realIndex)
    setTimeout(() => {
      this.fetching = false
    }, this.constructor.FADE_IN_MS)
  }

  updateActive(sessionId) {
    const total = this.totalValue
    this.dotTargets.forEach((dot) => {
      const isActive = dot.dataset.sessionId === String(sessionId)
      const wasActive = dot.dataset.active === "true"
      if (isActive === wasActive) return
      dot.dataset.active = isActive ? "true" : "false"
      dot.setAttribute("aria-current", isActive ? "true" : "false")
      this.styleDot(dot, isActive, total)
    })
    this.activeIdValue = String(sessionId)
    this.refreshChevrons()
  }

  styleDot(dot, isActive, total) {
    if (isActive) {
      const realIndex = parseInt(dot.dataset.realIndex, 10)
      const positionLabel =
        dot.dataset.positionLabel ||
        `${String(realIndex + 1).padStart(2, "0")}/${String(total).padStart(2, "0")}`
      dot.style.cssText =
        "width: 56px; background: rgba(167,139,250,0.95); box-shadow: 0 0 0 2px rgba(167,139,250,0.20), 0 0 12px rgba(167,139,250,0.35);"
      dot.innerHTML = `<span class="font-mono uppercase font-bold tracking-[0.18em] text-[9px] text-glossy-black-blue">${this.escape(positionLabel)}</span>`
    } else {
      dot.style.cssText = "width: 4px;"
      const tooltip = dot.getAttribute("aria-label") || ""
      dot.innerHTML = `<span class="session-scrubber-tooltip pointer-events-none absolute left-1/2 -translate-x-1/2 whitespace-nowrap font-mono uppercase opacity-0 group-hover/dot:opacity-100 transition-opacity duration-100 z-30" style="top: calc(100% + 8px); background: var(--scrubber-tooltip-bg); border: 1px solid var(--scrubber-tooltip-border); border-radius: 6px; padding: 6px 10px; font-size: 10px; line-height: 1.4; color: var(--scrubber-tooltip-text); box-shadow: 0 8px 20px rgba(0,0,0,0.35); letter-spacing: 0.04em;">${this.escape(tooltip)}</span>`
    }
  }

  refreshChevrons() {
    const idx = this.activeIndex()
    const canOlder = idx < this.totalValue - 1
    const canNewer = idx > 0
    this.toggleChevron(this.olderButtonTarget, canOlder)
    this.toggleChevron(this.newerButtonTarget, canNewer)
  }

  toggleChevron(button, enabled) {
    if (!button) return
    button.disabled = !enabled
  }

  wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  escape(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
